import React, { useEffect, useRef } from 'react';
import H from '@here/maps-api-for-javascript';
import { useDispatch } from 'react-redux';
import withTranslation from 'hoc/withTranslation';
import { colors } from 'global/variables';
import { enhanceStopGeoJSONFeatures } from '../NewStopMarkers/utils/stopMarkerHelpers';
import { getStopMarkerIcon } from '../NewStopMarkers/utils/stopMarkerFactory';
import { attachStopHoverEvents } from '../NewStopMarkers/utils/stopMarkerEvents';
import { areMarkerPropsEqual } from '../utils/markerPropsUtils';
import { createMarkerPopup } from '../NewJobMarkers/utils/markerPopupUtils';
import { MARKER_ANCHOR } from '../NewJobMarkers/utils/markerConstants';
import { buildStopClusterIcon, STOP_CLUSTER_ANCHOR } from './utils/clusterIconFactory';
import { buildStopClusterPopupHtml } from './utils/clusterPopupBuilder';

const CLUSTER_EPS = 50;
const SPIDER_RADIUS_PX = 60;
const SPIDER_LEG_COLOR = colors.transparentBlackColor;

function removeSpider(map, spiderRef) {
  if (!spiderRef.current) return;
  spiderRef.current.forEach((obj) => map.removeObject(obj));
  spiderRef.current = null;
}

function showSpider(map, cluster, spiderRef, translationsRef, dispatch, displayRef) {
  removeSpider(map, spiderRef);

  const features = [];
  cluster.forEachDataPoint((dp) => {
    const data = dp.getData();
    if (Array.isArray(data)) features.push(...data);
    else features.push(data);
  });
  const count = features.length;
  const center = map.geoToScreen(cluster.getPosition());
  const objects = [];

  features.forEach((feature, i) => {
    const angle = (2 * Math.PI * i) / count - Math.PI / 2;
    const sx = center.x + SPIDER_RADIUS_PX * Math.cos(angle);
    const sy = center.y + SPIDER_RADIUS_PX * Math.sin(angle);
    const geo = map.screenToGeo(sx, sy);

    const props = feature.properties;
    const svg = getStopMarkerIcon(feature, false);
    const marker = new window.H.map.DomMarker(geo, { volatility: true });
    marker.setData(props);
    marker.setVisibility(true);
    marker.setIcon(
      new window.H.map.DomIcon(
        `<div style="position:absolute;left:-${MARKER_ANCHOR.STOP.x}px;top:-${MARKER_ANCHOR.STOP.y}px;">${svg}</div>`,
      ),
    );
    attachStopHoverEvents(marker, translationsRef, map, dispatch, displayRef);

    const line = new window.H.map.Polyline(
      new window.H.geo.LineString([
        cluster.getPosition().lat, cluster.getPosition().lng, 0,
        geo.lat, geo.lng, 0,
      ]),
      { style: { strokeColor: SPIDER_LEG_COLOR, lineWidth: 1.5 } },
    );

    objects.push(line, marker);
    map.addObject(line);
    map.addObject(marker);
  });

  spiderRef.current = objects;
}

const NewClusteredStopMarkers = ({ translations, map, geoJSON, user, display }) => {
  const dispatch = useDispatch();
  const providerRef = useRef(null);
  const translationsRef = useRef(translations);
  const displayRef = useRef(display);
  const highlightRef = useRef({ id: display?.highlightStop, tourId: display?.selectedTour });
  const spiderRef = useRef(null);

  useEffect(() => {
    translationsRef.current = translations;
    displayRef.current = display;
    highlightRef.current = { id: display?.highlightStop, tourId: display?.selectedTour };
  });

  useEffect(() => {
    if (!map) return undefined;

    const theme = {
      getClusterPresentation: (cluster) => {
        const dataPoints = [];
        cluster.forEachDataPoint((dp) => {
          const data = dp.getData();
          if (Array.isArray(data)) dataPoints.push(...data.map((f) => f.properties));
          else dataPoints.push(data.properties);
        });

        const svgHtml = buildStopClusterIcon(dataPoints, highlightRef.current);
        const marker = new window.H.map.Marker(cluster.getPosition(), {
          min: cluster.getMinZoom(),
          max: cluster.getMaxZoom(),
          volatility: true,
        });
        marker.setIcon(new window.H.map.Icon(svgHtml, { anchor: STOP_CLUSTER_ANCHOR }));
        marker.setData({ isCluster: true, cluster });
        createMarkerPopup(marker, map, STOP_CLUSTER_ANCHOR.y, () =>
          buildStopClusterPopupHtml(dataPoints, translationsRef.current),
        );
        marker.addEventListener('tap', () => {
          const bbox = cluster.getBoundingBox();
          const currentZoom = map.getZoom();
          const maxZoom = map.getBaseLayer()?.getProvider()?.max ?? 20;
          if (bbox && currentZoom < maxZoom) {
            removeSpider(map, spiderRef);
            map.getViewModel().setLookAtData({ bounds: bbox }, true);
          } else {
            showSpider(map, cluster, spiderRef, translationsRef, dispatch, displayRef);
          }
        });
        return marker;
      },
      getNoisePresentation: (noisePoint) => {
        const data = noisePoint.getData();
        const features = Array.isArray(data) ? data : [data];
        const feature = features[0];
        const props = feature.properties;
        const hl = highlightRef.current;
        const highlight = hl?.id !== undefined && props.jobOrder === hl.id && props.routeId === hl.tourId;
        const svg = getStopMarkerIcon(feature, highlight);
        const marker = new window.H.map.DomMarker(
          { lat: feature.geometry.coordinates[1], lng: feature.geometry.coordinates[0] },
          { volatility: true, min: noisePoint.getMinZoom() },
        );
        marker.setData(props);
        marker.setVisibility(true);
        marker.setIcon(
          new window.H.map.DomIcon(
            `<div style="position:absolute;left:-${MARKER_ANCHOR.STOP.x}px;top:-${MARKER_ANCHOR.STOP.y}px;">${svg}</div>`,
          ),
        );
        attachStopHoverEvents(marker, translationsRef, map, dispatch, displayRef);
        return marker;
      },
    };

    const provider = new window.H.clustering.Provider([], {
      clusteringOptions: { eps: CLUSTER_EPS, minWeight: 2 },
      theme,
    });
    const clusterLayer = new H.map.layer.ObjectLayer(provider);
    map.addLayer(clusterLayer);
    providerRef.current = provider;

    const onMapViewChange = () => removeSpider(map, spiderRef);
    map.addEventListener('mapviewchange', onMapViewChange);

    return () => {
      map.removeEventListener('mapviewchange', onMapViewChange);
      removeSpider(map, spiderRef);
      map.removeLayer(clusterLayer);
      providerRef.current = null;
    };
  }, [map, dispatch]);

  useEffect(() => {
    if (!providerRef.current) return;

    if (
      !geoJSON?.geo?.features ||
      geoJSON.geo.features.length === 0 ||
      display?.showProblem ||
      !user?.mapSettings?.showStops
    ) {
      providerRef.current.setDataPoints([]);
      return;
    }

    const stopGeoJSON = enhanceStopGeoJSONFeatures(geoJSON, displayRef.current);
    const allFeatures = stopGeoJSON?.geo?.features?.filter((f) => f.geometry.type === 'Point') || [];

    if (allFeatures.length === 0) {
      providerRef.current.setDataPoints([]);
      return;
    }

    const groups = new Map();
    allFeatures.forEach((f) => {
      const key = `${f.geometry.coordinates[0]},${f.geometry.coordinates[1]}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(f);
    });

    const dataPoints = [...groups.entries()].map(([, features]) => {
      const [lng, lat] = features[0].geometry.coordinates;
      return new window.H.clustering.DataPoint(lat, lng, features.length, features);
    });

    providerRef.current.setDataPoints(dataPoints);
  }, [
    geoJSON,
    display?.showProblem,
    display?.showUnassigned,
    display?.selectedTour,
    display?.routeIds,
    display?.highlightStop,
    user?.mapSettings?.showStops,
  ]);

  return null;
};

const MemoizedNewClusteredStopMarkers = React.memo(NewClusteredStopMarkers, areMarkerPropsEqual);

export default withTranslation(MemoizedNewClusteredStopMarkers);
